// ===========================================
// Public Events List Page
// ===========================================
// Lists a school's published public upcoming events — no login required.
// Route: /:schoolSlug/events  (registered in App.tsx)

import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  Chip,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  SubscriptionsOutlined as SubscribeIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { usePublicEvents } from '../../hooks/useEvents';
import { schoolFeedIcsUrl } from '../../api/events.api';
import type { EventListItem } from '../../api/events.api';
import { tierPriceSummary } from '../../components/events/EventTierPricing';
import PublicFooter from '../../components/public/PublicFooter';
import {
  EVENT_TYPE_COLORS,
  EVENT_TYPE_LABELS,
  eventTypeChipTextColor,
} from '../../constants/eventTypes';

function formatEventDate(start: string, end: string): string {
  const s = parseISO(start);
  const e = parseISO(end);
  const day = format(s, 'EEEE d MMMM yyyy');
  const startTime = format(s, 'h:mm a');
  const endTime = format(e, 'h:mm a');
  return `${day} · ${startTime} – ${endTime}`;
}

// ===========================================
// EVENT CARD
// ===========================================

interface EventCardProps {
  event: EventListItem;
  schoolSlug: string;
}

function EventCard({ event, schoolSlug }: EventCardProps) {
  const typeColor = EVENT_TYPE_COLORS[event.type] ?? '#9DA5AF';

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: '2px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        boxShadow: 'none',
        bgcolor: 'background.paper',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      {event.featuredImage ? (
        <CardMedia
          component="img"
          height={180}
          image={event.featuredImage}
          alt={event.title}
          sx={{ objectFit: 'cover' }}
        />
      ) : (
        <Box
          sx={{
            height: 180,
            bgcolor: typeColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CalendarIcon sx={{ fontSize: 64, color: 'white', opacity: 0.6 }} />
        </Box>
      )}

      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip
            label={EVENT_TYPE_LABELS[event.type] ?? 'Event'}
            size="small"
            sx={{
              bgcolor: typeColor,
              color: eventTypeChipTextColor(event.type),
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
          {!event.isFree &&
            (() => {
              const price =
                tierPriceSummary(event.EventTicketTier) ??
                (event.ticketPriceCents != null
                  ? `$${(event.ticketPriceCents / 100).toFixed(2)}`
                  : null);
              return price ? (
                <Chip
                  label={price}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ) : null;
            })()}
          {event.isFree && (
            <Chip
              label="Free"
              size="small"
              variant="outlined"
              color="success"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.3 }}>
          {event.title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
          <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.3, flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary">
            {formatEventDate(event.startDateTime, event.endDateTime)}
          </Typography>
        </Box>

        {event.venueName && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.3, flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
              {event.venueName}
            </Typography>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button
          component={RouterLink}
          to={`/${schoolSlug}/events/${event.slug}`}
          variant="contained"
          size="small"
          fullWidth
        >
          View details
        </Button>
      </CardActions>
    </Card>
  );
}

// ===========================================
// PAGE COMPONENT
// ===========================================

export default function PublicEventsListPage() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const effectiveSlug = schoolSlug || 'music-n-me';

  const { data, isLoading, error } = usePublicEvents(effectiveSlug);
  const events = data?.data ?? [];
  const feedUrl = schoolFeedIcsUrl(effectiveSlug);

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          bgcolor: '#FCF6E6',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading events...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', py: 8 }}>
        <Container maxWidth="md">
          <Alert severity="error">
            Unable to load events. Please check the URL and try again.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#FCF6E6',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Character decoration - left */}
      <Box
        component="img"
        src="/character.png"
        alt=""
        sx={{
          position: 'fixed',
          left: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: 'calc(10% - 50px)', md: 'calc(20% - 50px)' },
          width: { xs: 120, md: 160, lg: 200 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(-10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Character decoration - right */}
      <Box
        component="img"
        src="/character-mint.png"
        alt=""
        sx={{
          position: 'fixed',
          right: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: '30%', md: '40%' },
          width: { xs: 100, md: 140, lg: 180 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Music notes - left */}
      <Box
        sx={{
          position: 'fixed',
          left: { xs: 10, md: 40 },
          top: 'calc(30% + 50px)',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%' }} />
      </Box>

      {/* Music notes - right */}
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 10, md: 40 },
          top: '50%',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%' }} />
      </Box>

      {/* Mobile header */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 2,
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{ maxWidth: 120, height: 'auto', mb: 2 }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'primary.main',
            textAlign: 'center',
            fontSize: '1.75rem',
          }}
        >
          Upcoming Events
        </Typography>
      </Box>

      {/* Desktop piano header */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'relative',
          width: '100%',
          mb: 3,
        }}
      >
        <Box
          component="img"
          src="/piano-header.png"
          alt="Piano keys"
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            position: 'absolute',
            top: { sm: 12, md: 16 },
            left: { sm: 12, md: 16 },
            maxWidth: { sm: 100, md: 120 },
            height: 'auto',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'rgba(8,8,8,0.45)',
            py: 1,
            px: 2,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
              color: 'white',
              textAlign: 'center',
              fontSize: { sm: '2rem', md: '2.5rem' },
            }}
          >
            Upcoming Events
          </Typography>
        </Box>
      </Box>

      {/* Subtitle + calendar subscribe */}
      <Container maxWidth="md" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: 'text.secondary', textAlign: { xs: 'center', sm: 'left' } }}>
            {events.length > 0
              ? `${events.length} upcoming event${events.length === 1 ? '' : 's'}`
              : 'Stay tuned for upcoming events'}
          </Typography>

          <Button
            component="a"
            href={feedUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            startIcon={<SubscribeIcon />}
            size="small"
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Subscribe to calendar
          </Button>
        </Box>
      </Container>

      {/* Content */}
      <Container maxWidth="lg" sx={{ pb: 6, position: 'relative', zIndex: 1, flex: 1 }}>
        {events.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 10,
              gap: 2,
            }}
          >
            <EventBusyIcon sx={{ fontSize: 72, color: 'text.disabled' }} />
            <Typography variant="h6" color="text.secondary" textAlign="center">
              No upcoming events at this time.
            </Typography>
            <Typography variant="body2" color="text.disabled" textAlign="center">
              Check back soon or subscribe to the calendar to be notified when events are added.
            </Typography>
            <Button
              component="a"
              href={feedUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              startIcon={<SubscribeIcon />}
            >
              Subscribe to calendar
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {events.map((event: EventListItem) => (
              <Grid item xs={12} sm={6} md={4} key={event.id}>
                <EventCard event={event} schoolSlug={effectiveSlug} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <PublicFooter schoolSlug={effectiveSlug} />
    </Box>
  );
}
